'use client';

import { Alert } from '../../../components/alert';
import { Button } from '../../../components/button';
import { Collapsible } from '../../../components/collapsible';
import { SettingsEditDialog } from '../SettingsEditDialog';
import {
  BodyDefault,
  BodyStrong,
  LabelDefault,
} from '../../../components/typography';
import { ToggleGroup } from '../../../components/toggle-group';
import { HStack, VStack } from '@terra/ui-styled-system';
import type {
  CvssEnvironmentalRequirements,
  CvssSecurityRequirementValue,
} from '@terra/shared-cvss';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  InformationCircleIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import {
  getModifiedMetricAllowedValues,
  getModifiedMetricKeysForVersion,
  getModifiedMetricValueLabelKey,
  type CvssModifiedMetricKey,
  type CvssVersionSetting,
} from './cvss-scoring-metrics';

type SegmentedToggleOption = {
  label: string;
  value: string;
};
type SegmentedToggleProps = {
  value: string;
  options: ReadonlyArray<SegmentedToggleOption>;
  disabled?: boolean;
  onChange: (value: string) => void;
};
type EditCvssScoreDialogProps = {
  open: boolean;
  isSaving: boolean;
  isDirty: boolean;
  draftVersion: CvssVersionSetting;
  draftRequirements: CvssEnvironmentalRequirements;
  showRescoreWarning?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  onVersionChange: (version: CvssVersionSetting) => void;
  onRequirementChange: (args: {
    key: keyof CvssEnvironmentalRequirements;
    value: string;
  }) => void;
};
const REQUIREMENT_DIALOG_VALUES = ['X', 'L', 'M', 'H'] as const;
const CIA_KEYS = ['cr', 'ir', 'ar'] as const;
const SegmentedToggle = ({
  value,
  options,
  disabled,
  onChange,
}: SegmentedToggleProps) => (
  <ToggleGroup.Root
    value={[value]}
    onValueChange={({ value: next }) => {
      const selected = next[0];

      if (selected) {
        onChange(selected);
      }
    }}
    disabled={disabled}
    variant="ghost"
    size="sm"
    w="full"
    gap={0}
    p="0.5"
    borderWidth="0"
    borderRadius="full"
    bg="bg.neutral.subtle"
    _horizontal={{ divideX: '0' }}
  >
    {options.map((option) => (
      <ToggleGroup.Item
        key={option.value}
        value={option.value}
        aria-label={option.label}
        flex={1}
        h="7"
        px="3"
        minW={0}
        borderWidth="0"
        borderRadius="full"
        fontSize="xs"
        fontWeight="medium"
        lineHeight="4"
        justifyContent="center"
        color="fg.neutral.muted"
        bg="transparent"
        shadow="none"
        boxShadow="none"
        _hover={{
          bg: 'transparent',
          color: 'fg.neutral.muted',
        }}
        _on={{
          bg: 'bg.neutral.muted',
          color: 'fg.neutral.default',
          shadow: 'sm',
          _hover: {
            bg: 'bg.neutral.muted',
            color: 'fg.neutral.default',
          },
        }}
      >
        {option.label}
      </ToggleGroup.Item>
    ))}
  </ToggleGroup.Root>
);
const LabeledSegmentedToggle = ({
  label,
  value,
  options,
  disabled,
  onChange,
}: SegmentedToggleProps & {
  label: string;
}) => (
  <VStack alignItems="stretch" gap="1.5" w="full">
    <LabelDefault color="fg.neutral.default">{label}</LabelDefault>
    <SegmentedToggle
      value={value}
      options={options}
      disabled={disabled}
      onChange={onChange}
    />
  </VStack>
);

export const EditCvssScoreDialog = ({
  open,
  isSaving,
  isDirty,
  draftVersion,
  draftRequirements,
  showRescoreWarning = true,
  onOpenChange,
  onSubmit,
  onVersionChange,
  onRequirementChange,
}: EditCvssScoreDialogProps) => {
  const t = useTranslations('designSystem.settings.cvssScoring');
  const [isAdditionalOpen, setIsAdditionalOpen] = useState(false);
  const modifiedMetricKeys = getModifiedMetricKeysForVersion(draftVersion);
  const versionOptions = useMemo(
    () => [
      { label: t('versions.v31'), value: 'V3_1' },
      { label: t('versions.v40'), value: 'V4_0' },
    ],
    [t],
  );
  const requirementOptions = useMemo(
    () =>
      REQUIREMENT_DIALOG_VALUES.map((value) => ({
        label: t(`requirements.${value}`),
        value,
      })),
    [t],
  );
  const metricValueLabel = ({
    key,
    value,
  }: {
    key: CvssModifiedMetricKey;
    value: string;
  }) =>
    t(
      getModifiedMetricValueLabelKey({ key, value }) as Parameters<typeof t>[0],
    );

  return (
    <SettingsEditDialog
      open={open}
      title={t('editScore')}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      loading={isSaving}
      isSubmitDisabled={!isDirty}
      contentMaxW="640px"
      contentMaxHeight="65vh"
    >
      <VStack alignItems="stretch" gap={4} w="full">
        {showRescoreWarning ? (
          <Alert.Root
            w="full"
            bg="bg.neutral.info"
            borderColor="border.neutral.info"
            borderWidth="1px"
            borderRadius="md"
            alignItems="center"
            p={2}
            gap={4}
          >
            <Alert.Icon asChild color="fg.neutral.info">
              <InformationCircleIcon size={20} />
            </Alert.Icon>
            <Alert.Content>
              <Alert.Description fontSize="sm" color="fg.neutral.default">
                {t('rescoreWarning')}
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        ) : null}

        <LabeledSegmentedToggle
          label={t('version')}
          value={draftVersion}
          options={versionOptions}
          disabled={isSaving}
          onChange={(value) => onVersionChange(value as CvssVersionSetting)}
        />

        <BodyStrong color="fg.neutral.default">
          {t('environmentalRequirements')}
        </BodyStrong>
        {CIA_KEYS.map((key) => (
          <LabeledSegmentedToggle
            key={key}
            label={t(key)}
            value={draftRequirements[key]}
            options={requirementOptions}
            disabled={isSaving}
            onChange={(value) =>
              onRequirementChange({
                key,
                value: value as CvssSecurityRequirementValue,
              })
            }
          />
        ))}

        <Collapsible.Root
          open={isAdditionalOpen}
          onOpenChange={({ open: nextOpen }) => setIsAdditionalOpen(nextOpen)}
        >
          <Collapsible.Trigger asChild>
            <Button
              variant="ghost"
              size="sm"
              justifyContent="flex-start"
              px={0}
              fontWeight="medium"
            >
              <HStack gap={2}>
                {isAdditionalOpen ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
                <BodyDefault fontWeight="medium">
                  {t('additionalModifiers')}
                </BodyDefault>
              </HStack>
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <VStack alignItems="stretch" gap={4} w="full" pt={4}>
              {modifiedMetricKeys.map((key) => {
                const allowedValues = getModifiedMetricAllowedValues({
                  key,
                  version: draftVersion,
                });
                const storedValue = draftRequirements[key];
                const selectValue = allowedValues.includes(storedValue)
                  ? storedValue
                  : 'X';

                return (
                  <LabeledSegmentedToggle
                    key={key}
                    label={t(`modified.${key}`)}
                    value={selectValue}
                    options={allowedValues.map((value) => ({
                      label: metricValueLabel({ key, value }),
                      value,
                    }))}
                    disabled={isSaving}
                    onChange={(value) => onRequirementChange({ key, value })}
                  />
                );
              })}
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      </VStack>
    </SettingsEditDialog>
  );
};
